import { get } from 'lodash';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Dropdown, ListView, SearchBar } from '@commons/utils/styledLibraryComponents';
import { Tags } from '@commons/Tags';
import EmptyState from '@commons/EmptyState';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { externalClient as externalClientService } from '@services/externalClient';

import ProductCountBadge from '../ProductCountBadge';

import { Container, HeaderContainer } from './styledComponents';

const DEFAULT_ORDER_BY = 'name';
const DEFAULT_ORDER_TYPE = 'asc';
const DEFAULT_MAX_PER_PAGE = 100;
const FIRST_PAGE = 1;

const getColumns = () => [
  {
    id: 'name',
    propertyKey: 'name',
    name: i18next.t('EXTERNAL_CLIENTS.CREATION.CLONE_COLUMN_CLIENT'),
    render: (name) => (
      <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
        {name}
      </Text>
    ),
  },
  {
    id: 'externalReference',
    propertyKey: 'externalReference',
    name: i18next.t('EXTERNAL_CLIENTS.CREATION.CLONE_COLUMN_EXTERNAL_REFERENCE'),
    render: (externalReference) => (
      <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
        {externalReference || '-'}
      </Text>
    ),
  },
  {
    id: 'stores',
    propertyKey: 'stores',
    name: i18next.t('EXTERNAL_CLIENTS.CREATION.CLONE_COLUMN_STORES'),
    disableSort: true,
    render: (stores) => <Tags items={(stores || []).map(({ name }) => name)} />,
  },
  {
    id: 'productCount',
    propertyKey: 'productCount',
    name: i18next.t('EXTERNAL_CLIENTS.CREATION.CLONE_COLUMN_PRODUCT_COUNT'),
    render: (productCount) => <ProductCountBadge productCount={productCount} />,
  },
];

/**
 * Decoupled catalog-clone picker: a paginated, searchable list of the client's external clients
 * (single-select) filtered by a store multi-select. Kept free of any modal knowledge so it can be
 * dropped into a standalone page later; it only reports the selected client id via onSelectClient.
 * The store filter is scoped to the stores of the connected user and starts with all of them
 * selected, so all their ids are sent on open.
 * `excludedClientId` is dropped from the rows client-side (the route has no exclusion parameter), so
 * the displayed total may exceed the rows of that single page by one.
 */
const CloneCatalogList = (props) => {
  const { stores, selectedClientId, excludedClientId, onSelectClient } = props;

  const listViewRef = useRef(null);
  const [columns] = useState(getColumns());

  const [externalClients, setExternalClients] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // Starts true so the very first render shows the loading state instead of a "no result" flash.
  const [isLoading, setIsLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [orderType, setOrderType] = useState(DEFAULT_ORDER_TYPE);
  const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
  const [maxPerPage, setMaxPerPage] = useState(DEFAULT_MAX_PER_PAGE);

  const [selectedStores, setSelectedStores] = useState(stores);

  const selectedStoreIds = selectedStores.map(({ id }) => id);

  const selectableClients = useMemo(
    () => externalClients.filter(({ id }) => id !== excludedClientId),
    [externalClients, excludedClientId],
  );

  // Selection is controlled by the parent so it survives modal refreshes; ListView reads the flag.
  const listData = useMemo(
    () =>
      selectableClients.map((externalClientRow) => ({
        ...externalClientRow,
        isRowSelected: externalClientRow.id === selectedClientId,
      })),
    [selectableClients, selectedClientId],
  );

  const fetchExternalClients = async () => {
    if (!selectedStoreIds.length) {
      setExternalClients([]);
      setTotalCount(0);
      setIsLoading(false);

      return;
    }

    setIsLoading(true);

    try {
      const { externalClients: data, totalCount: count } =
        await externalClientService.getPaginatedExternalClients(
          { page: currentPage, limit: maxPerPage, search: search || '', orderBy, orderType },
          { storeIds: selectedStoreIds },
        );

      setExternalClients(data || []);
      setTotalCount(count || 0);
    } catch {
      setExternalClients([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchExternalClients();
  }, [search, orderBy, orderType, currentPage, maxPerPage, selectedStores]);

  const handleSelection = (selectedItems) => {
    const clientId = get(selectedItems, '[0].id', null);

    if (clientId) {
      onSelectClient(clientId);

      return;
    }

    // An empty report has two causes: the user unselected the current row, or server-side paging
    // loaded a page that simply doesn't contain the chosen client. Only the former should clear the
    // selection, so reset when the chosen client is on the visible page (hence genuinely toggled off).
    const isSelectedClientOnCurrentPage = selectableClients.some(
      ({ id }) => id === selectedClientId,
    );

    if (selectedClientId && isSelectedClientOnCurrentPage) {
      onSelectClient(null);
    }
  };

  const handleSearchInputChange = (value) => {
    setSearch(value);
    setCurrentPage(FIRST_PAGE);
  };

  const handleMaxPerPageChange = (value) => {
    setMaxPerPage(value);
    setCurrentPage(FIRST_PAGE);
  };

  const handleStoreFilterChange = (items) => {
    setSelectedStores(items);
    setCurrentPage(FIRST_PAGE);
  };

  return (
    <Container>
      <ListView
        columns={columns}
        countElements={totalCount}
        data={listData}
        defaultCurrentPage={currentPage}
        defaultMaxPerPage={maxPerPage}
        defaultOrderBy={orderBy}
        defaultOrderType={orderType}
        defaultSearchInput={search}
        handleCurrentPageChange={setCurrentPage}
        handleMaxPerPageChange={handleMaxPerPageChange}
        handleOrderByChange={setOrderBy}
        handleOrderTypeChange={setOrderType}
        isLoading={isLoading}
        padding={'6px 24px 0px 24px'}
        ref={listViewRef}
        renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
        renderFilterButton={() => (
          <HeaderContainer>
            <SearchBar
              placeholder={i18next.t('GENERAL.SEARCH')}
              setValue={handleSearchInputChange}
              value={search}
            />
            <Dropdown
              iconSrc={'/images/inpulse/store-black-small.svg'}
              isUniqueSelection={false}
              items={stores}
              placeholder={i18next.t('EXTERNAL_CLIENTS.CREATION.CLONE_COLUMN_STORES')}
              selectedItems={selectedStores}
              isRequired
              onSelectionChange={handleStoreFilterChange}
            />
          </HeaderContainer>
        )}
        setSelectedItems={handleSelection}
        disableMultipleSelection
        hideAllPerPageOption
        hideSearchbar
        onQueryParamsChange={() => true}
      />
    </Container>
  );
};

CloneCatalogList.propTypes = {
  stores: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ).isRequired,
  selectedClientId: PropTypes.string,
  excludedClientId: PropTypes.string,
  onSelectClient: PropTypes.func.isRequired,
};

CloneCatalogList.defaultProps = {
  excludedClientId: null,
};

export default CloneCatalogList;
